import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
} from "react";
import { Container } from "@pixi/react";
import HoldAndWinProgressBarTensionOverlayController from "./HoldAndWinProgressBarTensionOverlayController";
import { safeInit } from "../../../utilities";

/**
 * Hold and Win Progress Bar Tension Overlay Component
 * Manages tension animation for progress bar milestones (15 and 20)
 * Completely independent from progress bar animations
 */
const HoldAndWinProgressBarTensionOverlay = forwardRef(
  ({ dimensions, symbolWidth, symbolHeight, device, orientation }, ref) => {
    const tensionControllerRef = useRef(null);
    const tensionContainerRef = useRef(null);

    // Initialize controller when assets are ready
    useEffect(() => {
      if (tensionContainerRef.current && !tensionControllerRef.current) {
        safeInit(() => {
          tensionControllerRef.current =
            new HoldAndWinProgressBarTensionOverlayController(
              tensionContainerRef.current,
              dimensions,
              symbolWidth,
              symbolHeight,
              device,
              orientation
            );
        });
      }

      return () => {
        if (tensionControllerRef.current) {
          tensionControllerRef.current.cleanup?.();
          tensionControllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle dimension changes
    useEffect(() => {
      if (
        tensionControllerRef.current &&
        symbolWidth &&
        symbolHeight &&
        dimensions
      ) {
        tensionControllerRef.current.updateDimensions(
          dimensions,
          symbolWidth,
          symbolHeight,
          device,
          orientation
        );
      }
    }, [symbolWidth, symbolHeight, dimensions, device, orientation]);

    // Expose controller methods to parent components
    useImperativeHandle(ref, () => ({
      playTensionIn: (milestone, orientation, playSound = true) => {
        tensionControllerRef.current?.playTensionIn(milestone, orientation, playSound);
      },

      playTensionOut: () => {
        tensionControllerRef.current?.playTensionOut();
      },

      hideTension: () => {
        tensionControllerRef.current?.hideTension();
      },

      forceStopTension: () => {
        tensionControllerRef.current?.forceStopTension();
      },

      resumeTensionSoundIfNeeded: () => {
        tensionControllerRef.current?.resumeTensionSoundIfNeeded();
      },
    }));

    return (
      <Container
        ref={tensionContainerRef}
        name="hold_and_win_progress_bar_tension_overlay"
        sortableChildren
      />
    );
  }
);

export default HoldAndWinProgressBarTensionOverlay;
